import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function QbrsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2">
          <div className="h-8 w-44 bg-muted rounded animate-pulse"></div>
          <div className="h-4 w-72 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="h-10 w-48 bg-muted rounded animate-pulse"></div>
      </div>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="h-10 flex-1 bg-muted rounded animate-pulse"></div>
            <div className="h-10 w-32 bg-muted rounded animate-pulse"></div>
            <div className="h-10 w-32 bg-muted rounded animate-pulse"></div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="p-4 animate-pulse">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-muted rounded w-1/3"></div>
                    <div className="flex gap-3">
                      <div className="h-4 bg-muted rounded w-28"></div>
                      <div className="h-4 bg-muted rounded w-24"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-20 bg-muted rounded"></div>
                    <div className="h-8 w-8 bg-muted rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plus,
  Search,
  Eye,
  Download,
  FileText,
  Calendar,
  ExternalLink,
  Presentation,
  CheckCircle,
  Trash2,
  MoreVertical,
  AlertCircle,
  Grid3X3,
  List,
  ArrowUpDown,
  UserCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/queryClient";

import QbrPresentation from "@/components/qbr/qbr-presentation";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/lib/rbac";
import { hasAnyRole } from "@/lib/authUtils";
import { ReassignDialog } from "@/components/ui/reassign-dialog";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { CreateBusinessReviewDialog } from "@/components/business-reviews/create-business-review-dialog";

export default function Qbrs() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [selectedQbr, setSelectedQbr] = useState<any>(null);
  
  const [showPresentationMode, setShowPresentationMode] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: qbrs, isLoading } = useQuery<any[]>({
    queryKey: ["/api/qbrs"],
  });

  const { data: customers } = useQuery<any[]>({
    queryKey: ["/api/customers"],
  });

  const { data: assessments } = useQuery<any[]>({
    queryKey: ["/api/assessments"],
  });

  // Use status endpoint accessible to all authenticated users
  const { data: integrations } = useQuery<any>({
    queryKey: ["/api/integrations/status"],
  });

  const getStatusBadge = (status: string) => {
    const statusMap = {
      draft: { label: "Draft", variant: "secondary" as const },
      finalized: { label: "Finalized", variant: "default" as const },
      scheduled: { label: "Scheduled", variant: "default" as const },
      presented: { label: "Presented", variant: "outline" as const },
      summary_sent: { label: "Summary Sent", variant: "default" as const },
    };

    const config = (statusMap as Record<string, any>)[status] || statusMap.draft;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getCustomerName = (customerId: number) => {
    const customer = customers?.find((c: any) => c.id === customerId);
    return customer?.name || `Customer ${customerId}`;
  };

  const canReassignQbr = (qbr: any) => {
    if (!user) return false;

    // Creator can always reassign
    if (qbr.createdById === user.id) return true;

    // vCIO+ roles can reassign
    if (hasAnyRole(user, ["vcio", "admin", "owner"])) return true;

    return false;
  };

  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/users/list"],
  });

  const getAssignedUserName = (assignedToId: string) => {
    const assignedUser = users?.find((u: any) => u.id === assignedToId);
    return assignedUser
      ? `${assignedUser.firstName} ${assignedUser.lastName}`
      : "Unknown User";
  };

  // Get approved assessments count for notification
  const approvedAssessmentsCount =
    assessments?.filter(
      (assessment: any) =>
        assessment.status === "approved" &&
        !qbrs?.some((qbr: any) => qbr.assessmentId === assessment.id),
    ).length || 0;

  

  const filteredQbrs =
    qbrs
      ?.filter((qbr: any) => {
        const matchesSearch =
          getCustomerName(qbr.customerId)
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          qbr.title.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
          statusFilter === "all" || qbr.status === statusFilter;

        const matchesAssignedTo =
          assignedToFilter === "all" || qbr.assignedToId === assignedToFilter;

        return matchesSearch && matchesStatus && matchesAssignedTo;
      })
      .sort((a: any, b: any) => {
        let aValue, bValue;

        switch (sortBy) {
          case "title":
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          case "customer":
            aValue = getCustomerName(a.customerId).toLowerCase();
            bValue = getCustomerName(b.customerId).toLowerCase();
            break;
          case "status":
            aValue = a.status;
            bValue = b.status;
            break;
          case "scheduledDate":
            aValue = a.scheduledDate ? new Date(a.scheduledDate) : new Date(0);
            bValue = b.scheduledDate ? new Date(b.scheduledDate) : new Date(0);
            break;
          case "createdAt":
          default:
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
        }

        if (sortOrder === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      }) || [];

  // Mutations
  const markFinalizedMutation = useMutation({
    mutationFn: async (qbrId: number) => {
      await apiRequest("PATCH", `/api/qbrs/${qbrId}`, { status: "finalized" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      toast({ title: "Success", description: "Business Review marked as finalized" });
    },
  });

  const markPresentedMutation = useMutation({
    mutationFn: async (qbrId: number) => {
      await apiRequest("PATCH", `/api/qbrs/${qbrId}`, { status: "presented" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      toast({ title: "Success", description: "Business Review marked as presented" });
    },
  });

  const deleteQbrMutation = useMutation({
    mutationFn: async (qbrId: number) => {
      await apiRequest("DELETE", `/api/qbrs/${qbrId}`);
      return qbrId;
    },
    onMutate: async (qbrId: number) => {
      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ["/api/qbrs"] });
      const previousQbrs = queryClient.getQueryData(["/api/qbrs"]);
      // Optimistically remove the item from the cache
      queryClient.setQueryData(["/api/qbrs"], (old: any[] | undefined) => {
        if (!old) return old;
        return old.filter((qbr: any) => qbr.id !== qbrId);
      });
      return { previousQbrs, deletedId: qbrId };
    },
    onSuccess: (_data, qbrId) => {
      // Remove from cache directly instead of refetching to avoid race condition
      queryClient.setQueryData(["/api/qbrs"], (old: any[] | undefined) => {
        if (!old) return old;
        return old.filter((qbr: any) => qbr.id !== qbrId);
      });
      // Also invalidate the specific QBR query if it exists
      queryClient.removeQueries({ queryKey: ["/api/qbrs", qbrId] });
      toast({ title: "Success", description: "Business Review deleted successfully" });
    },
    onError: (error: any, _qbrId, context) => {
      // Rollback to previous state on error
      if (context?.previousQbrs) {
        queryClient.setQueryData(["/api/qbrs"], context.previousQbrs);
      }
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to delete Business Review",
        variant: "destructive",
      });
    },
  });

  // Loading state - must be after all hooks
  if (isLoading) {
    return (
      <div>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 md:h-8 md:w-8" />
            Business Reviews
          </h1>
          <p className="text-muted-foreground">
            Manage business review reports
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-onboarding="generate-qbr">
          <Plus className="h-4 w-4 mr-2" />
          Create Business Review
        </Button>
      </div>

      {/* Filters and Controls */}
      <Card className="mb-4">
        <CardContent className="p-4 md:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search Business Reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="w-full sm:w-48"
                data-onboarding="qbr-status-filter"
              >
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="finalized">Finalized</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="presented">Presented</SelectItem>
                <SelectItem value="summary_sent">Summary Sent</SelectItem>
              </SelectContent>
            </Select>

            {/* Assigned To Filter */}
            <Select
              value={assignedToFilter}
              onValueChange={setAssignedToFilter}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Assigned to" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users?.map((user: any) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Date Created</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="scheduledDate">Scheduled Date</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="w-20"
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortBy === "createdAt" || sortBy === "scheduledDate"
                ? (sortOrder === "asc" ? "Old" : "New")
                : (sortOrder === "asc" ? "A-Z" : "Z-A")
              }
            </Button>

            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="rounded-r-none"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Reviews Display */}
      <Card data-onboarding="qbr-list">
        <CardHeader>
          <CardTitle>All Business Reviews</CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {viewMode === "grid" ? (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredQbrs.length > 0 ? (
                filteredQbrs.map((qbr: any) => (
                  <Card
                    key={qbr.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-green-50 dark:bg-green-950 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="h-6 w-6 text-green-600 dark:text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3
                              className="font-semibold text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                              onClick={() => setLocation(`/qbrs/${qbr.id}`)}
                            >
                              {qbr.title}
                            </h3>
                            <div data-onboarding="qbr-status">
                              {getStatusBadge(qbr.status)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            Customer:
                          </span>
                          <span className="text-foreground font-medium">
                            {getCustomerName(qbr.customerId)}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {canReassignQbr(qbr) && (
                          <ReassignDialog
                            type="qbr"
                            itemId={qbr.id}
                            itemTitle={qbr.title}
                            currentAssigneeId={qbr.assignedToId}
                            currentAssigneeName={getAssignedUserName(
                              qbr.assignedToId,
                            )}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title="Reassign QBR"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          </ReassignDialog>
                        )}
                        {hasRole(user, ["admin", "vcio", "owner"]) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (
                                confirm(
                                  `Are you sure you want to delete "${qbr.title}"? This action cannot be undone.`,
                                )
                              ) {
                                deleteQbrMutation.mutate(qbr.id);
                              }
                            }}
                            title="Delete QBR"
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex justify-between items-center text-xs text-muted-foreground">
                          <span>
                            Created{" "}
                            {new Date(qbr.createdAt).toLocaleDateString()}
                          </span>
                          <span>ID: {qbr.id}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-6">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No Business Reviews found
                  </h3>
                  <p className="text-muted-foreground">
                    {searchTerm || statusFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Start by generating your first Business Review from an approved assessment."}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // List View
            <div>
              {/* Mobile Cards - Show on small screens */}
              <div className="block lg:hidden p-4">
                {filteredQbrs.length > 0 ? (
                  <div className="space-y-3">
                    {filteredQbrs.map((qbr: any) => (
                      <div
                        key={qbr.id}
                        className="p-4 bg-muted/30 rounded-lg border"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0 flex-1">
                            <h3
                              className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline truncate"
                              onClick={() => setLocation(`/qbrs/${qbr.id}`)}
                            >
                              {qbr.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              ID: {qbr.id}
                            </p>
                          </div>
                          <div
                            className="ml-3 flex-shrink-0"
                            data-onboarding="qbr-status"
                          >
                            {getStatusBadge(qbr.status)}
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Customer:
                            </span>
                            <span className="text-foreground">
                              {getCustomerName(qbr.customerId)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Assigned To:
                            </span>
                            <span className="text-foreground">
                              {getAssignedUserName(qbr.assignedToId)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Created:
                            </span>
                            <span className="text-foreground">
                              {new Date(qbr.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {canReassignQbr(qbr) && (
                            <ReassignDialog
                              type="qbr"
                              itemId={qbr.id}
                              itemTitle={qbr.title}
                              currentAssigneeId={qbr.assignedToId}
                              currentAssigneeName={getAssignedUserName(
                                qbr.assignedToId,
                              )}
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="Reassign Business Review"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            </ReassignDialog>
                          )}
                          {hasRole(user, ["admin", "vcio", "owner"]) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to delete "${qbr.title}"? This action cannot be undone.`,
                                  )
                                ) {
                                  deleteQbrMutation.mutate(qbr.id);
                                }
                              }}
                              title="Delete Business Review"
                              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No Business Reviews found
                    </h3>
                    <p className="text-muted-foreground">
                      {searchTerm || statusFilter !== "all"
                        ? "Try adjusting your search or filter criteria."
                        : "Start by generating your first Business Review from an approved assessment."}
                    </p>
                  </div>
                )}
              </div>

              {/* Desktop Table - Show on large screens */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-background">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "title") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("title");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Business Review</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "title" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "customer") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("customer");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Customer</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "customer" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          data-onboarding="status-header"
                          onClick={() => {
                            if (sortBy === "status") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("status");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Status</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "status" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "createdAt") {
                              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            } else {
                              setSortBy("createdAt");
                              setSortOrder("desc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Created</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "createdAt" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-gray-200">
                      {filteredQbrs.length > 0 ? (
                        filteredQbrs.map((qbr: any) => (
                          <tr key={qbr.id}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div>
                                <div
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                                  onClick={() => setLocation(`/qbrs/${qbr.id}`)}
                                >
                                  {qbr.title}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  ID: {qbr.id}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="text-sm text-foreground">
                                {getCustomerName(qbr.customerId)}
                              </div>
                            </td>
                            <td
                              className="px-4 py-4 whitespace-nowrap"
                              data-onboarding="qbr-status"
                            >
                              {getStatusBadge(qbr.status)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {getAssignedUserName(qbr.assignedToId)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {new Date(qbr.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              <div className="flex items-center space-x-2">
                                {canReassignQbr(qbr) && (
                                  <ReassignDialog
                                    type="qbr"
                                    itemId={qbr.id}
                                    itemTitle={qbr.title}
                                    currentAssigneeId={qbr.assignedToId}
                                    currentAssigneeName={getAssignedUserName(
                                      qbr.assignedToId,
                                    )}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                      title="Reassign Business Review"
                                    >
                                      <UserCheck className="h-4 w-4" />
                                    </Button>
                                  </ReassignDialog>
                                )}
                                {hasRole(user, ["admin", "vcio", "owner"]) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Are you sure you want to delete "${qbr.title}"? This action cannot be undone.`,
                                        )
                                      ) {
                                        deleteQbrMutation.mutate(qbr.id);
                                      }
                                    }}
                                    title="Delete Business Review"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center">
                            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-foreground mb-2">
                              No Business Reviews found
                            </h3>
                            <p className="text-muted-foreground">
                              {searchTerm || statusFilter !== "all"
                                ? "Try adjusting your search or filter criteria."
                                : "Start by generating your first Business Review from an approved assessment."}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {showPresentationMode && selectedQbr && (
        <Dialog
          open={showPresentationMode}
          onOpenChange={setShowPresentationMode}
        >
          <DialogContent className="max-w-6xl max-h-[95vh]">
            <QbrPresentation
              qbr={selectedQbr}
              customer={customers?.find(
                (c: any) => c.id === selectedQbr.customerId,
              )}
              onClose={() => {
                setShowPresentationMode(false);
                setSelectedQbr(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Create Business Review Dialog */}
      <CreateBusinessReviewDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}
